import React, { useState, useEffect } from 'react';
import { Plus, Target, CheckCircle2, Clock, AlertTriangle, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { db } from '../../../lib/db';
import { useGoals } from '../../../hooks/useDatabase';
import { formatDate, getDaysUntil, isOverdue } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { useConfirmDialog } from '../../shared/ConfirmDialog';

export default function GoalsPage() {
  const { user } = useAuthStore();
  const { confirm } = useConfirmDialog();
  const { createGoal, updateGoal, deleteGoal, loading: goalLoading } = useGoals();
  const [filter, setFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newGoal, setNewGoal] = useState({ title: '', description: '', priority: 'medium', dueDate: '' });
  const [editingGoalId, setEditingGoalId] = useState(null);

  useEffect(() => { loadData(); }, [user]);

  const loadData = async () => {
    try {
      const goalsData = await db.goals.toArray();
      setGoals(goalsData.filter(g => g.userId === user?.id));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const allGoals = goals;
  const filteredGoals = allGoals.filter(goal => {
    if (filter === 'all') return true;
    if (filter === 'active') return goal.status !== 'completed';
    if (filter === 'completed') return goal.status === 'completed';
    return goal.type === filter;
  });

  const stats = {
    total: allGoals.length,
    completed: allGoals.filter(g => g.status === 'completed').length,
    inProgress: allGoals.filter(g => g.status === 'in_progress').length,
    notStarted: allGoals.filter(g => g.status === 'not_started').length
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'in_progress': return <Clock className="w-5 h-5 text-blue-500" />;
      case 'blocked': return <AlertTriangle className="w-5 h-5 text-rose-500" />;
      default: return <Target className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'blocked': return 'bg-rose-100 text-rose-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-rose-100 text-rose-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const handleSaveGoal = async () => {
    if (!newGoal.title) {
      toast.error('Please enter a goal title');
      return;
    }
    const dueDateValue = newGoal.dueDate ? new Date(newGoal.dueDate).getTime() : Date.now() + 30 * 24 * 60 * 60 * 1000;
    try {
      if (editingGoalId) {
        await updateGoal(editingGoalId, {
          title: newGoal.title,
          description: newGoal.description,
          priority: newGoal.priority,
          dueDate: dueDateValue,
          updatedAt: Date.now()
        });
        toast.success('Goal updated successfully!');
      } else {
        await createGoal({
          userId: user.id,
          title: newGoal.title,
          description: newGoal.description,
          priority: newGoal.priority,
          dueDate: dueDateValue,
          status: 'not_started',
          type: 'current'
        });
        toast.success('Goal added successfully!');
      }
      setShowAddModal(false);
      setNewGoal({ title: '', description: '', priority: 'medium', dueDate: '' });
      setEditingGoalId(null);
      loadData();
    } catch (error) {
      toast.error('Failed to save goal');
    }
  };

  const openEditGoal = (goal) => {
    const dueDateInput = goal.dueDate ? new Date(goal.dueDate).toISOString().slice(0, 10) : '';
    setNewGoal({
      title: goal.title || '',
      description: goal.description || '',
      priority: goal.priority || 'medium',
      dueDate: dueDateInput
    });
    setEditingGoalId(goal.id);
    setShowAddModal(true);
  };

  const handleUpdateGoalStatus = async (goalId, newStatus) => {
    try {
      await updateGoal(goalId, { status: newStatus });
      toast.success('Goal updated!');
      loadData();
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    const confirmed = await confirm({
      title: 'Delete Goal',
      description: 'Are you sure you want to delete this goal?',
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (confirmed) {
      try {
        await deleteGoal(goalId);
        toast.success('Goal deleted');
        loadData();
      } catch (error) {
        toast.error('Failed to delete goal');
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Goals</h1>
          <p className="text-gray-500 mt-1">Track and manage your performance goals</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-cyan-600 hover:bg-cyan-700 text-white"><Plus className="w-4 h-4 mr-2" /> Add Goal</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center"><Target className="w-5 h-5 text-violet-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{stats.total}</p><p className="text-xs text-gray-500">Total Goals</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-blue-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{stats.inProgress}</p><p className="text-xs text-gray-500">In Progress</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{stats.completed}</p><p className="text-xs text-gray-500">Completed</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-cyan-600">{stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%</p>
              <p className="text-xs text-gray-500">Completion Rate</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Filter goals" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Goals</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="current">Current Period</SelectItem>
              <SelectItem value="future">Future</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {filteredGoals.map(goal => {
          const daysUntil = getDaysUntil(goal.dueDate);
          const overdue = isOverdue(goal.dueDate) && goal.status !== 'completed';
          return (
            <Card key={goal.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  {getStatusIcon(goal.status)}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{goal.title}</h3>
                        <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => openEditGoal(goal)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-rose-500" onClick={() => handleDeleteGoal(goal.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <Badge className={getStatusColor(goal.status)}>{goal.status?.replace('_', ' ')}</Badge>
                      <Badge className={getPriorityColor(goal.priority)}>{goal.priority}</Badge>
                      <Badge variant="secondary">{goal.type}</Badge>
                      <span className={`text-xs ${overdue ? 'text-rose-600' : 'text-gray-400'}`}>
                        Due: {formatDate(goal.dueDate)}{overdue && ' (Overdue)'}
                      </span>
                      <span className="text-xs text-gray-400">Created: {formatDate(goal.createdAt || goal.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      {goal.status !== 'completed' && (
                        <>
                          {goal.status !== 'in_progress' && (
                            <Button size="sm" variant="outline" onClick={() => handleUpdateGoalStatus(goal.id, 'in_progress')}>Start</Button>
                          )}
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleUpdateGoalStatus(goal.id, 'completed')}>Complete</Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredGoals.length === 0 && (
        <div className="text-center py-12">
          <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No goals found</p>
          <Button variant="link" onClick={() => setShowAddModal(true)} className="text-cyan-600">Add your first goal</Button>
        </div>
      )}

      <Dialog open={showAddModal} onOpenChange={(open) => { setShowAddModal(open); if (!open) { setEditingGoalId(null); setNewGoal({ title: '', description: '', priority: 'medium', dueDate: '' }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGoalId ? 'Edit Goal' : 'Add New Goal'}</DialogTitle>
            <DialogDescription>{editingGoalId ? 'Update this performance goal' : 'Create a new performance goal'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input placeholder="Goal title" value={newGoal.title} onChange={(e) => setNewGoal({...newGoal, title: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea placeholder="Describe your goal..." value={newGoal.description} onChange={(e) => setNewGoal({...newGoal, description: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={newGoal.priority} onValueChange={(v) => setNewGoal({...newGoal, priority: v})}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input type="date" value={newGoal.dueDate} onChange={(e) => setNewGoal({...newGoal, dueDate: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleSaveGoal} disabled={goalLoading} className="bg-cyan-600 hover:bg-cyan-700 text-white">{goalLoading ? 'Saving...' : editingGoalId ? 'Save Changes' : 'Add Goal'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
